import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Copy, Mail } from "lucide-react";
import { workspaceApi } from "@/api/workspace-api";
import { slugify, validateSlug } from "@/lib/slug-utils";
import { INBOX_EMAIL_DOMAIN } from "@/lib/constants/inbox";
import { useWorkspaceSettings } from "@/hooks/use-workspace-settings";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

type SlugStatus =
  | "idle"
  | "checking"
  | "available"
  | "taken"
  | "invalid"
  | "saving"
  | "saved";

export default function InboxSettings() {
  const { workspace, isLoading, activeWorkspaceId, invalidateWorkspace } =
    useWorkspaceSettings();

  const [inboundEmailEnabled, setInboundEmailEnabled] = useState(false);
  const [emailSlug, setEmailSlug] = useState("");
  const [isTogglingEmail, setIsTogglingEmail] = useState(false);
  const [emailSlugStatus, setEmailSlugStatus] = useState<SlugStatus>("idle");
  const [emailSlugError, setEmailSlugError] = useState("");
  const emailSlugCheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedEmailSlug = useRef("");

  useEffect(() => {
    if (!workspace) return;
    setInboundEmailEnabled(workspace.inbound_email_enabled || false);
    setEmailSlug(workspace.inbound_email_slug || "");
    lastSavedEmailSlug.current = workspace.inbound_email_slug || "";
  }, [workspace]);

  const workspaceEmail = emailSlug ? `${emailSlug}@${INBOX_EMAIL_DOMAIN}` : null;

  const handleToggleInboundEmail = async (enabled: boolean) => {
    setIsTogglingEmail(true);
    try {
      const updateData: Record<string, unknown> = { inbound_email_enabled: enabled };
      if (enabled && !emailSlug && workspace?.opportunity_name) {
        const generatedSlug = slugify(workspace.opportunity_name);
        let existingSlugs: string[] = [];
        try {
          existingSlugs = await workspaceApi.fetchInboundEmailSlugs();
        } catch {
          /* proceed */
        }
        let finalSlug = generatedSlug;
        let counter = 2;
        while (existingSlugs.includes(finalSlug)) {
          finalSlug = `${generatedSlug}-${counter}`;
          counter++;
        }
        updateData.inbound_email_slug = finalSlug;
        setEmailSlug(finalSlug);
        lastSavedEmailSlug.current = finalSlug;
      }
      await workspaceApi.updateWorkspace(activeWorkspaceId || "", updateData);
      setInboundEmailEnabled(enabled);
      invalidateWorkspace();
      toast.success(enabled ? "Inbound email enabled" : "Inbound email disabled");
    } catch {
      toast.error("Failed to update inbound email setting");
    } finally {
      setIsTogglingEmail(false);
    }
  };

  const handleEmailSlugChange = (value: string) => {
    const newSlug = value.toLowerCase().replace(/[^a-z0-9-]/g, "");
    setEmailSlug(newSlug);
    if (emailSlugCheckTimer.current) clearTimeout(emailSlugCheckTimer.current);
    if (!newSlug) {
      setEmailSlugStatus("idle");
      setEmailSlugError("");
      return;
    }
    const validation = validateSlug(newSlug);
    if (!validation.valid) {
      setEmailSlugStatus("invalid");
      setEmailSlugError(validation.error || "Invalid format");
      return;
    }
    if (newSlug === lastSavedEmailSlug.current) {
      setEmailSlugStatus("saved");
      setEmailSlugError("");
      return;
    }
    setEmailSlugStatus("checking");
    setEmailSlugError("");
    emailSlugCheckTimer.current = setTimeout(async () => {
      try {
        const existingSlugs = await workspaceApi.fetchInboundEmailSlugs();
        const isTaken = existingSlugs.some(
          (s) => s === newSlug && newSlug !== lastSavedEmailSlug.current
        );
        setEmailSlugStatus(isTaken ? "taken" : "available");
        setEmailSlugError(isTaken ? "This email address is already taken" : "");
      } catch {
        setEmailSlugStatus("available");
        setEmailSlugError("");
      }
    }, 500);
  };

  const handleEmailSlugSave = async () => {
    if (emailSlugCheckTimer.current) clearTimeout(emailSlugCheckTimer.current);
    if (!emailSlug && lastSavedEmailSlug.current) {
      setEmailSlug(lastSavedEmailSlug.current);
      setEmailSlugStatus("idle");
      toast.error("Email slug cannot be empty. Restored to previous value.");
      return;
    }
    if (emailSlugStatus === "invalid" || emailSlugStatus === "taken") return;
    if (emailSlug === lastSavedEmailSlug.current) {
      setEmailSlugStatus("saved");
      return;
    }
    const validation = validateSlug(emailSlug);
    if (!validation.valid) {
      setEmailSlugStatus("invalid");
      setEmailSlugError(validation.error || "Invalid format");
      return;
    }
    setEmailSlugStatus("saving");
    try {
      const existingSlugs = await workspaceApi.fetchInboundEmailSlugs();
      const isTaken = existingSlugs.some(
        (s) => s === emailSlug && emailSlug !== lastSavedEmailSlug.current
      );
      if (isTaken) {
        setEmailSlugStatus("taken");
        setEmailSlugError("This email address is already taken");
        return;
      }
      await workspaceApi.updateWorkspace(activeWorkspaceId || "", {
        inbound_email_slug: emailSlug,
      });
      lastSavedEmailSlug.current = emailSlug;
      invalidateWorkspace();
      setEmailSlugStatus("saved");
      toast.success(`Email updated to ${emailSlug}@${INBOX_EMAIL_DOMAIN}`);
    } catch {
      setEmailSlugStatus("idle");
      toast.error("Failed to save. Please try again.");
    }
  };

  const renderSlugStatus = (status: SlugStatus, error: string) => {
    if (status === "idle") return null;
    const colorClass =
      status === "checking" || status === "saving"
        ? "text-muted-foreground"
        : status === "available" || status === "saved"
          ? "text-green-600"
          : "text-red-500";
    return (
      <div className={`flex items-center gap-1.5 text-xs mt-1 ${colorClass}`}>
        {(status === "checking" || status === "saving") && (
          <>
            <Loader2 className="h-3 w-3 animate-spin" />
            {status === "checking" ? "Checking availability..." : "Saving..."}
          </>
        )}
        {status === "available" && <><span>&check;</span> Available</>}
        {status === "saved" && <><span>&check;</span> Saved</>}
        {(status === "taken" || status === "invalid") && (
          <><span>&times;</span> {error}</>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-6 py-8">
      <h1 className="text-lg font-semibold text-foreground">Inbox</h1>
      <p className="mt-0.5 text-sm text-muted-foreground">
        Configure how deals and messages arrive in your workspace inbox.
      </p>

      <section className="mt-8">
        <h2 className="mb-3 text-xs font-medium uppercase tracking-widest text-muted-foreground/50">
          Inbound Email
        </h2>
        <div className="rounded-lg border border-border bg-card p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Mail className="w-5 h-5 text-primary" />
              </div>
              <div>
                <Label>Inbound Email</Label>
                <p className="text-sm text-muted-foreground">
                  Receive deals via a dedicated email address
                </p>
              </div>
            </div>
            <Switch
              checked={inboundEmailEnabled}
              disabled={isTogglingEmail}
              onCheckedChange={handleToggleInboundEmail}
            />
          </div>
          {inboundEmailEnabled && (
            <div className="space-y-4">
              {workspaceEmail && (
                <div className="space-y-1.5">
                  <Label>Workspace Email</Label>
                  <div className="flex gap-2 items-center">
                    <Input
                      readOnly
                      value={workspaceEmail}
                      className="flex-1 bg-muted font-mono text-sm"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => {
                        navigator.clipboard.writeText(workspaceEmail);
                        toast.success("Email address copied!");
                      }}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Emails sent to this address will be forwarded to your
                    workspace
                  </p>
                </div>
              )}
              <div className="space-y-1.5">
                <Label>Customize Email Address</Label>
                <p className="text-xs text-muted-foreground">
                  Lowercase letters, numbers, and hyphens only
                </p>
                <div className="flex gap-2 items-center">
                  <Input
                    value={emailSlug}
                    onChange={(e) => handleEmailSlugChange(e.target.value)}
                    onBlur={handleEmailSlugSave}
                    placeholder="your-workspace"
                    className={`flex-1 font-mono text-sm ${
                      emailSlugStatus === "taken" ||
                      emailSlugStatus === "invalid"
                        ? "border-red-400 focus-visible:ring-red-400"
                        : emailSlugStatus === "available" ||
                            emailSlugStatus === "saved"
                          ? "border-green-400 focus-visible:ring-green-400"
                          : ""
                    }`}
                    maxLength={50}
                  />
                  <span className="text-sm text-muted-foreground whitespace-nowrap">
                    @{INBOX_EMAIL_DOMAIN}
                  </span>
                </div>
                {renderSlugStatus(emailSlugStatus, emailSlugError)}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
